import { useReducer, useEffect, useRef } from 'react'
import { generateSession, startConversation, forfeitConversation, getStats, getLearningSettings, ApiError } from '../lib/api'
import { streamAnswer } from '../lib/sseStream'
import { trackSessionStarted, trackQuestionCompleted, trackSessionComplete, trackPaywallViewed } from '../lib/analyticsEvents'
import type { QuizSession, QuestionWithContext, AnswerResult, ConfidenceLevel, LearningSettings, MasteryLevel, HighlightRef } from '../types/models'
import { reducer, toUserMessage, attemptedCount, mergeMilestones } from './quizReducer'
import { loadSaved, save, clearSaved } from '../lib/quizStorage'

export { clearSavedQuizSession } from '../lib/quizStorage'
export { attemptedCount, mergeMilestones } from './quizReducer'

export interface ConversationTurn {
  userAnswer: string
  result: AnswerResult
  confidenceLabel?: string
}

// One run of streamed text per SSE segment kind, kept apart so the follow-up question can be styled alone.
export interface StreamingSegment {
  kind: 'delta' | 'followup' | 'closing'
  text: string
}

export interface QuestionResult {
  questionText: string
  specificTopic: string
  bookTitle: string
  score: number
  isCorrect: boolean
  // afterStage is null only on legacy responses lacking growthStage.
  beforeStage: MasteryLevel
  afterStage: MasteryLevel | null
  nextReviewInDays: number
  sourceQuote?: string | null
  sourceHighlight?: HighlightRef | null
  // Never graded (ADR-036): must not count toward "N of M". See attemptedCount.
  expired: boolean
  // Session-scoped milestones; stats alone cannot report them. See mergeMilestones.
  newMilestones: string[]
}

export type QuizPhase =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  // Free plan's daily session cap. Distinct from 'error' so the UI shows the upgrade prompt, not "Try again".
  | { phase: 'limit' }
  | { phase: 'empty'; nextReviewInHours: number }
  | {
      phase: 'summary'
      session: QuizSession
    }
  | {
      phase: 'conversation'
      session: QuizSession
      currentIndex: number
      jitQuestion: QuestionWithContext | null
      conversationSessionId: string | null
      conversationHistory: ConversationTurn[]
      streamingText: string | null
      streamingSegments: StreamingSegment[]
      isRetrying: boolean
      draftAnswer: string
      confidence: ConfidenceLevel | null
      conversationEnded: boolean
      correctCount: number
      questionResults: QuestionResult[]
      forfeited: boolean
      isStartingConversation: boolean
      conversationError: string | null
      isFirstSession: boolean
    }
  | {
      phase: 'complete'
      correctCount: number
      totalCount: number
      nextReviewInHours: number | null
      questionResults: QuestionResult[]
      newMilestones: string[]
      dailyGoalMet: boolean
      isFirstSession: boolean
    }


export function useQuizSession(token: string | null) {
  const [state, dispatch] = useReducer(reducer, { phase: 'loading' })
  const abortRef = useRef<AbortController | null>(null)
  const settingsRef = useRef<LearningSettings | null>(null)

  useEffect(() => {
    const saved = loadSaved()
    if (saved && (saved.phase === 'conversation' || saved.phase === 'summary' || saved.phase === 'complete')) {
      dispatch({ type: 'SET_PHASE', payload: saved })
      // No session id means the start request died with the tab: restart the question.
      if (saved.phase === 'conversation' && !saved.conversationSessionId) {
        startConversationForQuestion(saved, saved.currentIndex)
      }
    } else {
      loadSession()
    }
    return () => { abortRef.current?.abort() }
    // Mount-once: the callbacks are unmemoized, so listing them would re-run them every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stateRef = useRef(state)
  stateRef.current = state

  const convHistLength = state.phase === 'conversation' ? state.conversationHistory.length : -1
  useEffect(() => {
    const currentState = stateRef.current
    if (currentState.phase === 'conversation' && currentState.conversationHistory.length > 0) {
      save(currentState)
    }
  }, [convHistLength])

  async function loadSession() {
    dispatch({ type: 'SET_PHASE', payload: { phase: 'loading' } })
    clearSaved()
    try {
      if (!settingsRef.current) {
        settingsRef.current = await getLearningSettings().catch(() => null)
      }
      const sessionSize = settingsRef.current?.sessionSize ?? 10
      const session = await generateSession(sessionSize, true, true)
      // Standalone getStats() call covers a backend that does not bundle stats.
      const stats = session.stats ?? (await Promise.resolve(getStats()).catch(() => null))
      // No stats means not first: a transient error must never strip a returning user's summary.
      const isFirstSession = stats != null && stats.sessionsCompleted === 0
      if (session.concepts.length === 0) {
        dispatch({
          type: 'SET_PHASE',
          payload: { phase: 'empty', nextReviewInHours: stats?.nextReviewInHours ?? 0 },
        })
      } else if (isFirstSession) {
        beginConversation(session, true)
      } else {
        const next: QuizPhase = { phase: 'summary', session }
        dispatch({ type: 'SET_PHASE', payload: next })
        save(next)
      }
    } catch (e) {
      // The only 402 from POST /quiz/session is the free daily cap: show the upgrade prompt.
      if (e instanceof ApiError && e.code === 'PAYMENT_REQUIRED') {
        trackPaywallViewed('session_cap')
        dispatch({ type: 'SET_PHASE', payload: { phase: 'limit' } })
      } else {
        dispatch({
          type: 'SET_PHASE',
          payload: { phase: 'error', message: e instanceof ApiError ? e.message : 'Failed to load session' },
        })
      }
    }
  }

  async function startConversationForQuestion(currentState: QuizPhase & { phase: 'conversation' }, index: number) {
    const questionId = currentState.session.concepts[index].question.id
    try {
      const response = await startConversation(questionId)
      dispatch({ type: 'CONVERSATION_STARTED', payload: { sessionId: response.sessionId, jitQuestion: response.question } })
      save({ ...currentState, conversationSessionId: response.sessionId, jitQuestion: response.question, isStartingConversation: false })
    } catch (e) {
      dispatch({
        type: 'SET_PHASE',
        payload: {
          phase: 'error',
          message: e instanceof ApiError ? e.message : 'Failed to start conversation',
        },
      })
    }
  }

  // Shared by the Begin tap and the first-session skip, so session_started fires once either way.
  function beginConversation(session: QuizSession, isFirstSession: boolean) {
    const firstQ = session.concepts[0]
    const next: QuizPhase = {
      phase: 'conversation',
      session,
      currentIndex: 0,
      jitQuestion: null,
      conversationSessionId: null,
      conversationHistory: [],
      streamingText: null,
      streamingSegments: [],
      isRetrying: false,
      draftAnswer: '',
      confidence: null,
      conversationEnded: false,
      correctCount: 0,
      questionResults: [],
      forfeited: false,
      isStartingConversation: true,
      conversationError: null,
      isFirstSession,
    }
    dispatch({ type: 'SET_PHASE', payload: next })
    save(next)
    trackSessionStarted(session.concepts.length)

    startConversation(firstQ.question.id)
      .then(response => {
        dispatch({ type: 'CONVERSATION_STARTED', payload: { sessionId: response.sessionId, jitQuestion: response.question } })
        const updated: QuizPhase = { ...next, conversationSessionId: response.sessionId, jitQuestion: response.question, isStartingConversation: false }
        save(updated)
      })
      .catch(e => {
        dispatch({
          type: 'SET_PHASE',
          payload: { phase: 'error', message: e instanceof ApiError ? e.message : 'Failed to start conversation' },
        })
      })
  }

  function startSession() {
    const s = state
    if (s.phase !== 'summary') return
    beginConversation(s.session, false)
  }

  function setDraftAnswer(text: string) {
    dispatch({ type: 'SET_DRAFT', payload: text })
  }

  async function submitAnswer(confidence: ConfidenceLevel, confidenceLabel?: string) {
    if (state.phase !== 'conversation') return
    const { conversationSessionId, draftAnswer } = state
    if (!conversationSessionId || !draftAnswer.trim() || !token) return

    dispatch({ type: 'SET_STREAMING', payload: '' })

    abortRef.current?.abort()
    abortRef.current = new AbortController()

    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null
    let timedOut = false

    const submission = { sessionId: conversationSessionId, userAnswer: draftAnswer, confidence }
    try {
      for await (const event of streamAnswer(submission, token, abortRef.current.signal)) {
        if (event.type === 'delta' || event.type === 'followup' || event.type === 'closing') {
          dispatch({
            type: 'APPEND_STREAMING',
            payload: { kind: event.type, text: event.text },
          })
        } else if (event.type === 'complete') {
          if (retryTimeoutId) { clearTimeout(retryTimeoutId); retryTimeoutId = null }
          dispatch({ type: 'ANSWER_COMPLETE', payload: { result: event.result, confidenceLabel } })
          if (event.result.conversationEnded) {
            trackQuestionCompleted(
              state.currentIndex,
              event.result.score,
              event.result.isCorrect ?? event.result.score >= 50,
              event.result.expired ?? false,
            )
            const s = state as Extract<QuizPhase, { phase: 'conversation' }>
            save({ ...s, conversationEnded: true })
          }
        } else if (event.type === 'error') {
          if (retryTimeoutId) { clearTimeout(retryTimeoutId); retryTimeoutId = null }
          dispatch({ type: 'SET_CONVERSATION_ERROR', payload: toUserMessage(event.message) })
        } else if (event.type === 'retrying') {
          dispatch({ type: 'SET_RETRYING', payload: true })
          if (!retryTimeoutId) {
            retryTimeoutId = setTimeout(() => {
              timedOut = true
              abortRef.current?.abort()
              dispatch({ type: 'SET_CONVERSATION_ERROR', payload: 'timeout' })
            }, 5 * 60 * 1000)
          }
        }
      }
    } catch {
      if (retryTimeoutId) { clearTimeout(retryTimeoutId) }
      if (!timedOut) {
        dispatch({ type: 'SET_CONVERSATION_ERROR', payload: 'Connection error. Please try again.' })
      }
    }
  }

  async function skipQuestion() {
    if (state.phase !== 'conversation') return
    const { conversationSessionId } = state
    if (!conversationSessionId) return

    try {
      const result = await forfeitConversation(conversationSessionId)
      const s = state
      const concept = s.jitQuestion ?? s.session.concepts[s.currentIndex]
      const newQuestionResults: QuestionResult[] = [
        ...s.questionResults,
        {
          questionText: concept.question.questionText,
          specificTopic: concept.question.specificTopic,
          bookTitle: s.session.concepts[s.currentIndex].bookTitle,
          score: 0,
          isCorrect: false,
          beforeStage: concept.growthStage,
          afterStage: result.growthStage ?? null,
          nextReviewInDays: result.nextReviewIn,
          sourceQuote: result.sourceQuote ?? null,
          sourceHighlight: result.sourceHighlight ?? null,
          // A forfeit may hit an already-abandoned session (ADR-036): its placeholders are not an attempt.
          expired: result.expired ?? false,
          newMilestones: result.newMilestones ?? [],
        },
      ]
      const skippedState: QuizPhase = {
        ...s,
        conversationHistory: [...s.conversationHistory, { userAnswer: '(skipped)', result }],
        conversationEnded: true,
        forfeited: true,
        questionResults: newQuestionResults,
        streamingText: null,
        streamingSegments: [],
      }
      dispatch({ type: 'SET_PHASE', payload: skippedState })
      save(skippedState)
    } catch { /* ignore */ }
  }

  async function nextQuestion() {
    if (state.phase !== 'conversation') return
    const nextIndex = state.currentIndex + 1

    if (nextIndex >= state.session.concepts.length) {
      clearSaved()
      let nextReviewInHours: number | null = null
      let statsMilestones: string[] = []
      let dailyGoalMet = false
      try {
        const stats = await getStats()
        nextReviewInHours = stats.nextReviewInHours
        statsMilestones = stats.newMilestones ?? []
        dailyGoalMet = stats.dailyGoalMet ?? false
      } catch { /* ignore */ }

      const totalCount = attemptedCount(state.session.concepts.length, state.questionResults)
      const newMilestones = mergeMilestones(
        state.questionResults.map(r => r.newMilestones),
        statsMilestones,
      )
      const complete: QuizPhase = {
        phase: 'complete',
        correctCount: state.correctCount,
        totalCount,
        nextReviewInHours,
        questionResults: state.questionResults,
        newMilestones,
        dailyGoalMet,
        isFirstSession: state.isFirstSession,
      }
      trackSessionComplete(state.correctCount, totalCount, dailyGoalMet)
      dispatch({ type: 'SET_PHASE', payload: complete })
    } else {
      const nextQ = state.session.concepts[nextIndex]
      const next: QuizPhase = {
        ...state,
        currentIndex: nextIndex,
        jitQuestion: null,
        conversationSessionId: null,
        conversationHistory: [],
        streamingText: null,
        streamingSegments: [],
        isRetrying: false,
        draftAnswer: '',
        confidence: null,
        conversationEnded: false,
        forfeited: false,
        isStartingConversation: true,
        conversationError: null,
      }
      dispatch({ type: 'SET_PHASE', payload: next })
      save(next)

      startConversation(nextQ.question.id)
        .then(response => {
          dispatch({ type: 'CONVERSATION_STARTED', payload: { sessionId: response.sessionId, jitQuestion: response.question } })
          const updated = { ...next, conversationSessionId: response.sessionId, jitQuestion: response.question, isStartingConversation: false }
          save(updated)
        })
        .catch(e => {
          dispatch({
            type: 'SET_PHASE',
            payload: { phase: 'error', message: e instanceof ApiError ? e.message : 'Failed to start next question' },
          })
        })
    }
  }

  function clearConversationError() {
    dispatch({ type: 'CLEAR_CONVERSATION_ERROR' })
  }

  return { state, startSession, setDraftAnswer, submitAnswer, skipQuestion, nextQuestion, refresh: loadSession, clearConversationError }
}
